import { useMemo, useState } from 'react'
import { useAllQuizTopics } from '@/lib/use-all-topics'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { CircleAlert, FileSpreadsheet, Plus, RefreshCw, Send } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  getV1MeRetrieveQueryOptions,
  getV1QuizAdminQuestionsListQueryKey,
  getV1QuizAdminTopicsListQueryKey,
  useV1QuizAdminQuestionsList,
  useV1QuizAdminCapabilities,
  useV1QuizAdminQuestionsBulkPublish,
  v1QuizAdminQuestionImportsCommitCreate,
  v1QuizAdminQuestionImportsPreviewCreate,
  v1QuizAdminQuestionImportsTemplateRetrieve,
} from '@/api/client'
import {
  V1QuizAdminQuestionsListDifficulty,
  V1QuizAdminQuestionsListModule,
  V1QuizAdminQuestionsListPool,
  V1QuizAdminQuestionsListStatus,
  V1QuizAdminQuestionsListType,
} from '@/api/model'
import type { AdminQuestion, AdminQuestionBulkPublishFilterRequest } from '@/api/model'
import { PageHeader } from '@/components/page-header'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DataTable, type Column, type StateOption } from '@/components/data-table'
import { queryHasHttpError } from '@/lib/query-state'
import { QuestionStatusBadge } from '@/components/quiz-bank/question-status-badge'
import {
  QuestionImportDialog,
  type QuestionImportApi,
} from '@/components/quiz-bank/question-import-dialog'
import { CONTENT_MODULE_OPTIONS, moduleLabel } from '@/lib/activation-labels'
import { difficultyLabel, poolLabel, typeLabel } from '@/lib/quiz-labels'
import { headRevision, questionExcerpt } from '@/lib/quiz-excerpt'
import { QuizCapabilityNotice } from '@/components/quiz/quiz-capability-notice'
import {
  canAuthorQuiz,
  canPublishQuiz,
  quizCapabilitiesOrClosed,
} from '@/components/quiz/quiz-capabilities'

export const Route = createFileRoute('/_panel/pytania/')({
  beforeLoad: async ({ context }) => {
    // Operator-only area (admin + superadmin, broader than konta-administracyjne):
    // everyone else is bounced out before any question data loads (FR-002).
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: QuestionBankPage,
})

const PAGE_SIZE = 25
const ALL = 'all'

type StatusFilter = typeof ALL | V1QuizAdminQuestionsListStatus

export function isQuestionSelected(
  uid: string,
  selectedUids: ReadonlySet<string>,
  allFilteredCount: number | null,
): boolean {
  return allFilteredCount !== null || selectedUids.has(uid)
}

function countLabel(count: number, singular: string, paucal: string, plural: string): string {
  if (count === 1) return `${count} ${singular}`
  const lastTwo = count % 100
  const last = count % 10
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return `${count} ${paucal}`
  }
  return `${count} ${plural}`
}

const statusOptions: StateOption<StatusFilter>[] = [
  { value: ALL, label: 'Wszystkie' },
  { value: V1QuizAdminQuestionsListStatus.draft, label: 'Szkice' },
  { value: V1QuizAdminQuestionsListStatus.published, label: 'Opublikowane' },
  { value: V1QuizAdminQuestionsListStatus.retired, label: 'Wycofane' },
]

function QuestionBankPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [importOpen, setImportOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<StatusFilter>(ALL)
  const [type, setType] = useState<string>(ALL)
  const [difficulty, setDifficulty] = useState<string>(ALL)
  const [pool, setPool] = useState<string>(ALL)
  const [module, setModule] = useState<string>(ALL)
  // Temat wybiera się ze słownika, nie wpisuje (spec 088) — filtr jest selectem,
  // więc nie da się szukać wariantu, którego nikt nigdy nie zapisał.
  const [topic, setTopic] = useState<string>(ALL)
  const [selectedUids, setSelectedUids] = useState<Set<string>>(() => new Set())
  const [allFilteredCount, setAllFilteredCount] = useState<number | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [bulkMessage, setBulkMessage] = useState<string | null>(null)
  const capabilitiesQuery = useV1QuizAdminCapabilities()
  const capabilities = quizCapabilitiesOrClosed(
    capabilitiesQuery.data?.status === 200 ? capabilitiesQuery.data.data : undefined,
  )
  const canAuthor = canAuthorQuiz(capabilities)
  const canPublish = canPublishQuiz(capabilities)
  const bulkPublish = useV1QuizAdminQuestionsBulkPublish()
  const topicsQuery = useAllQuizTopics({ is_active: true })
  const topicOptions = topicsQuery.items
  const questionImportApi: QuestionImportApi = {
    downloadTemplate: async () => {
      const response = await v1QuizAdminQuestionImportsTemplateRetrieve()
      if (response.status !== 200)
        throw new Error('Nie udało się pobrać szablonu. Spróbuj ponownie.')
      return response.data
    },
    validate: async (file) => {
      const response = await v1QuizAdminQuestionImportsPreviewCreate({ file })
      if (response.status !== 201)
        throw new Error('Nie udało się sprawdzić arkusza. Spróbuj ponownie.')
      return response.data
    },
    commit: async (importId) => {
      const response = await v1QuizAdminQuestionImportsCommitCreate(importId)
      if (response.status !== 200) {
        throw new Error('Nie udało się utworzyć szkiców. Żadne pytanie nie zostało zaimportowane.')
      }
      return response.data
    },
  }

  // Server-side filtering, search and paging (FR-004): every control maps to a
  // wire param; the backend searches head-revision text (incl. drafts).
  const query = useV1QuizAdminQuestionsList({
    page,
    page_size: PAGE_SIZE,
    search: search.trim() || undefined,
    status: status === ALL ? undefined : status,
    type: type === ALL ? undefined : (type as V1QuizAdminQuestionsListType),
    difficulty: difficulty === ALL ? undefined : (difficulty as V1QuizAdminQuestionsListDifficulty),
    pool: pool === ALL ? undefined : (pool as V1QuizAdminQuestionsListPool),
    module: module === ALL ? undefined : (module as V1QuizAdminQuestionsListModule),
    topic: topic === ALL ? undefined : topic,
  })
  // The visible "Wszystkie" scope mixes drafts with already published or retired
  // questions. Bulk publishing still targets drafts only, so it needs its own exact
  // count for the optimistic concurrency guard sent as `expected_count`.
  const draftCountQuery = useV1QuizAdminQuestionsList(
    {
      page: 1,
      page_size: 1,
      search: search.trim() || undefined,
      status: V1QuizAdminQuestionsListStatus.draft,
      type: type === ALL ? undefined : (type as V1QuizAdminQuestionsListType),
      difficulty:
        difficulty === ALL ? undefined : (difficulty as V1QuizAdminQuestionsListDifficulty),
      pool: pool === ALL ? undefined : (pool as V1QuizAdminQuestionsListPool),
      module: module === ALL ? undefined : (module as V1QuizAdminQuestionsListModule),
      topic: topic === ALL ? undefined : topic,
    },
    { query: { enabled: status === ALL } },
  )
  const data = query.data?.status === 200 ? query.data.data : undefined
  const draftCountData =
    draftCountQuery.data?.status === 200 ? draftCountQuery.data.data : undefined
  const draftCountFailed =
    status === ALL &&
    (draftCountQuery.isError ||
      (draftCountQuery.data !== undefined && draftCountQuery.data.status !== 200))
  const filteredDraftCount =
    status === V1QuizAdminQuestionsListStatus.draft
      ? (data?.count ?? 0)
      : status === ALL
        ? (draftCountData?.count ?? 0)
        : 0
  // `customFetch` never throws on HTTP errors — a non-200 status IS the failure
  // (ui-contract Bank-list States: error note + retry, never the empty message).
  const failed = query.isError || (query.data !== undefined && query.data.status !== 200)
  const draftRows = useMemo(
    () => (data?.results ?? []).filter((question) => question.status === 'draft'),
    [data?.results],
  )
  const pageDraftUids = useMemo(() => draftRows.map((question) => question.uid), [draftRows])
  const allPageDraftsSelected =
    pageDraftUids.length > 0 && pageDraftUids.every((uid) => selectedUids.has(uid))
  const somePageDraftsSelected = pageDraftUids.some((uid) => selectedUids.has(uid))
  const selectionCount = allFilteredCount ?? selectedUids.size
  const resetSelection = () => {
    setSelectedUids(new Set())
    setAllFilteredCount(null)
  }
  const setFilter = <T,>(setter: (value: T) => void, value: T) => {
    setter(value)
    resetSelection()
    setBulkMessage(null)
  }
  const filterScope: AdminQuestionBulkPublishFilterRequest = {
    search: search.trim() || undefined,
    status: V1QuizAdminQuestionsListStatus.draft,
    type: type === ALL ? undefined : (type as V1QuizAdminQuestionsListType),
    difficulty: difficulty === ALL ? undefined : (difficulty as V1QuizAdminQuestionsListDifficulty),
    pool: pool === ALL ? undefined : (pool as V1QuizAdminQuestionsListPool),
    module: module === ALL ? undefined : (module as V1QuizAdminQuestionsListModule),
    topic: topic === ALL ? undefined : topic,
  }
  const selectPageDrafts = (checked: boolean | 'indeterminate') => {
    if (allFilteredCount !== null) {
      resetSelection()
      return
    }
    if (checked === 'indeterminate' || !checked) {
      setSelectedUids((current) => {
        const next = new Set(current)
        pageDraftUids.forEach((uid) => next.delete(uid))
        return next
      })
      return
    }
    setSelectedUids((current) => new Set([...current, ...pageDraftUids]))
  }
  const toggleDraft = (uid: string, checked: boolean | 'indeterminate') => {
    setSelectedUids((current) => {
      const next = new Set(current)
      if (checked === true) next.add(uid)
      else next.delete(uid)
      return next
    })
  }
  const publishSelected = async () => {
    setBulkMessage(null)
    const payload =
      allFilteredCount !== null
        ? {
            selection_mode: 'filter' as const,
            filters: filterScope,
            expected_count: allFilteredCount,
          }
        : {
            selection_mode: 'ids' as const,
            question_uids: [...selectedUids],
            expected_count: selectedUids.size,
          }
    try {
      const response = await bulkPublish.mutateAsync({ data: payload })
      if (response.status === 200) {
        setBulkMessage(
          `Opublikowano ${response.data.published_count} z ${response.data.matched_count} ${response.data.matched_count === 1 ? 'pytania' : 'pytań'}.`,
        )
        setConfirmOpen(false)
        resetSelection()
        setPage(1)
        await queryClient.invalidateQueries({ queryKey: getV1QuizAdminQuestionsListQueryKey() })
      } else if (response.status === 409) {
        setBulkMessage(
          'Zakres pytań zmienił się w międzyczasie. Odświeżono listę — zaznacz je ponownie.',
        )
        setConfirmOpen(false)
        resetSelection()
        setPage(1)
        await queryClient.invalidateQueries({ queryKey: getV1QuizAdminQuestionsListQueryKey() })
      } else {
        setBulkMessage('Nie udało się opublikować pytań. Spróbuj ponownie.')
      }
    } catch {
      setBulkMessage('Nie udało się opublikować pytań. Spróbuj ponownie.')
    }
  }
  const columns: Column<AdminQuestion>[] = [
    {
      header: (
        <Checkbox
          aria-label="Zaznacz pytania na tej stronie"
          checked={
            allFilteredCount !== null
              ? true
              : allPageDraftsSelected
                ? true
                : somePageDraftsSelected
                  ? 'indeterminate'
                  : false
          }
          disabled={!canPublish || pageDraftUids.length === 0}
          onCheckedChange={selectPageDrafts}
        />
      ),
      cell: (q) =>
        q.status === 'draft' ? (
          <Checkbox
            aria-label={`Zaznacz pytanie ${q.uid}`}
            checked={isQuestionSelected(q.uid, selectedUids, allFilteredCount)}
            disabled={!canPublish || allFilteredCount !== null}
            onCheckedChange={(checked) => toggleDraft(q.uid, checked)}
            onClick={(event) => event.stopPropagation()}
          />
        ) : null,
    },
    {
      header: 'Treść',
      cell: (q) => <span className="font-medium">{questionExcerpt(q)}</span>,
    },
    {
      header: 'Typ',
      cell: (q) => <span className="text-muted-foreground">{typeLabel(q.type)}</span>,
    },
    { header: 'Status', cell: (q) => <QuestionStatusBadge status={q.status} /> },
    {
      header: 'Poziom',
      cell: (q) => {
        const head = headRevision(q)
        return (
          <span className="text-muted-foreground">
            {head ? difficultyLabel(head.difficulty) : '—'}
          </span>
        )
      },
    },
    {
      header: 'Pula',
      cell: (q) => (
        <span className="text-muted-foreground">{q.pool ? poolLabel(q.pool) : '—'}</span>
      ),
    },
    {
      header: 'Moduły',
      cell: (q) =>
        q.modules && q.modules.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {q.modules.map((m) => (
              <Badge key={m} variant="secondary">
                {moduleLabel(m)}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      header: 'Tematy',
      cell: (q) =>
        q.topics && q.topics.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {q.topics.map((t) => (
              <Badge key={t.slug} variant={t.is_primary ? 'default' : 'secondary'}>
                {t.name}
              </Badge>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      header: 'Zaktualizowano',
      cell: (q) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {new Date(q.updated_at).toLocaleDateString('pl-PL')}
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Bank pytań"
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canAuthor}
              title={!canAuthor ? 'Autorowanie quizów jest wyłączone na serwerze.' : undefined}
              onClick={() => setImportOpen(true)}
            >
              <FileSpreadsheet data-icon="inline-start" /> Importuj arkusz
            </Button>
            <Button
              size="sm"
              disabled={!canAuthor}
              title={!canAuthor ? 'Autorowanie quizów jest wyłączone na serwerze.' : undefined}
              onClick={() => navigate({ to: '/pytania/nowe' })}
            >
              <Plus data-icon="inline-start" /> Nowe pytanie
            </Button>
          </div>
        }
      />
      <div className="p-6">
        <QuizCapabilityNotice capabilities={capabilities} />
        {bulkMessage && (
          <Alert className="mb-4" role="status">
            <AlertDescription>{bulkMessage}</AlertDescription>
          </Alert>
        )}
        {selectionCount > 0 && canPublish && (
          <Card role="region" aria-label="Akcje zaznaczonych pytań" className="mb-4">
            <CardHeader>
              <CardTitle>Masowe publikowanie</CardTitle>
              <CardDescription>
                Zaznaczono {countLabel(selectionCount, 'pytanie', 'pytania', 'pytań')}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {status === ALL &&
                allFilteredCount === null &&
                allPageDraftsSelected &&
                draftCountQuery.isLoading && (
                  <p className="text-sm text-muted-foreground">Liczenie wszystkich szkiców…</p>
                )}
              {allFilteredCount === null && allPageDraftsSelected && draftCountFailed && (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    Nie udało się policzyć wszystkich szkiców. Możesz opublikować zaznaczoną stronę
                    albo spróbować ponownie.
                  </p>
                  <Button
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    onClick={() => void draftCountQuery.refetch()}
                  >
                    Spróbuj ponownie
                  </Button>
                </div>
              )}
              {allFilteredCount === null &&
                allPageDraftsSelected &&
                !draftCountFailed &&
                filteredDraftCount > pageDraftUids.length &&
                filteredDraftCount <= 5000 && (
                  <Button
                    variant="link"
                    className="mt-2 h-auto p-0"
                    onClick={() => setAllFilteredCount(filteredDraftCount)}
                  >
                    Zaznacz wszystkie szkice spełniające filtry ({filteredDraftCount})
                  </Button>
                )}
              {allFilteredCount === null &&
                allPageDraftsSelected &&
                !draftCountFailed &&
                filteredDraftCount > 5000 && (
                  <p className="text-sm text-muted-foreground">
                    Filtry obejmują {filteredDraftCount} szkiców. Zawęź je, aby zaznaczyć
                    maksymalnie 5000 naraz.
                  </p>
                )}
            </CardContent>
            <CardFooter className="justify-end gap-2">
              <Button variant="outline" size="sm" onClick={resetSelection}>
                Wyczyść
              </Button>
              <Button size="sm" onClick={() => setConfirmOpen(true)}>
                <Send data-icon="inline-start" /> Opublikuj
              </Button>
            </CardFooter>
          </Card>
        )}
        {failed ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border bg-background py-14 text-center">
            <CircleAlert className="size-6 text-destructive" />
            <p className="text-sm text-muted-foreground">
              Nie udało się pobrać listy pytań. Spróbuj ponownie.
            </p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              <RefreshCw data-icon="inline-start" /> Spróbuj ponownie
            </Button>
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={[...(data?.results ?? [])]}
            rowKey={(q) => q.uid}
            count={data?.count ?? 0}
            page={page}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
            search={search}
            onSearchChange={(v) => {
              setFilter(setSearch, v)
              setPage(1)
            }}
            searchPlaceholder="Szukaj w treści pytań…"
            stateValue={status}
            stateOptions={statusOptions}
            onStateChange={(v) => {
              setFilter(setStatus, v)
              setPage(1)
            }}
            filters={
              <>
                <Select
                  value={type}
                  onValueChange={(v) => {
                    setFilter(setType, v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger size="sm" className="w-44">
                    <SelectValue placeholder="Wszystkie typy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Wszystkie typy</SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListType.pairs}>
                      {typeLabel('pairs')}
                    </SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListType.trueFalse}>
                      {typeLabel('trueFalse')}
                    </SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListType.singleChoice}>
                      {typeLabel('singleChoice')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={difficulty}
                  onValueChange={(v) => {
                    setFilter(setDifficulty, v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger size="sm" className="w-40">
                    <SelectValue placeholder="Wszystkie poziomy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Wszystkie poziomy</SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListDifficulty.easy}>
                      {difficultyLabel('easy')}
                    </SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListDifficulty.medium}>
                      {difficultyLabel('medium')}
                    </SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListDifficulty.hard}>
                      {difficultyLabel('hard')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={pool}
                  onValueChange={(v) => {
                    setFilter(setPool, v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger size="sm" className="w-36">
                    <SelectValue placeholder="Wszystkie pule" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Wszystkie pule</SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListPool.main}>
                      {poolLabel('main')}
                    </SelectItem>
                    <SelectItem value={V1QuizAdminQuestionsListPool.weekend}>
                      {poolLabel('weekend')}
                    </SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={module}
                  onValueChange={(v) => {
                    setFilter(setModule, v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger size="sm" className="w-44">
                    <SelectValue placeholder="Wszystkie moduły" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Wszystkie moduły</SelectItem>
                    {CONTENT_MODULE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={topic}
                  onValueChange={(v) => {
                    setFilter(setTopic, v)
                    setPage(1)
                  }}
                >
                  <SelectTrigger size="sm" className="w-44">
                    <SelectValue placeholder="Wszystkie tematy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>Wszystkie tematy</SelectItem>
                    {topic !== ALL && !topicOptions.some((o) => o.slug === topic) && (
                      <SelectItem value={topic}>{topic}</SelectItem>
                    )}
                    {topicOptions.map((o) => (
                      <SelectItem key={o.slug} value={o.slug}>
                        {o.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            }
            isLoading={query.isLoading}
            isError={queryHasHttpError(query)}
            onRetry={() => void query.refetch()}
            emptyMessage="Brak pytań spełniających kryteria."
            onRowClick={(q) => navigate({ to: '/pytania/$uid', params: { uid: q.uid } })}
            countNoun={['pytanie', 'pytań']}
          />
        )}
      </div>
      <QuestionImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        api={questionImportApi}
        onImported={() =>
          Promise.all([
            queryClient.invalidateQueries({
              queryKey: getV1QuizAdminQuestionsListQueryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: getV1QuizAdminTopicsListQueryKey(),
            }),
          ]).then(() => undefined)
        }
      />
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Opublikować {countLabel(selectionCount, 'pytanie', 'pytania', 'pytań')}?
            </DialogTitle>
            <DialogDescription>
              Pytania staną się dostępne dla quizów automatycznych. Operacja obejmuje{' '}
              {allFilteredCount !== null
                ? 'wszystkie wyniki bieżącego filtra.'
                : 'wybrane pytania.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={bulkPublish.isPending}
            >
              Anuluj
            </Button>
            <Button onClick={() => void publishSelected()} disabled={bulkPublish.isPending}>
              {bulkPublish.isPending && <Spinner data-icon="inline-start" />}
              Opublikuj
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
